import { StyleSheet, Text, View } from "@react-pdf/renderer";
import Html from "react-pdf-html";

import * as cheerio from "cheerio";

import { DEFAULT_PDF_CSS_STYLE } from "components/pdf-generation-v3/pdf-generation-v3.utils";

export type PdfTableCell = {
  content: string;
  colspan: number;
  // percentage width, will be calculated from table columns definition
  widthPercent?: number;
  isHeader?: boolean;
  rawHtml?: string;
  fontSizePx?: number;
  italic?: boolean;
  textAlign?: "left" | "center" | "right";
  backgroundColor?: string;
  textColor?: string;
};

export type PdfTableRow = {
  cells: PdfTableCell[];
};

export type PdfTableDescription = {
  columns: number[]; // percentage width per column, sums to 100
  rows: PdfTableRow[];
  borderColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted";
  borderWidth?: number;
};

const DEFAULT_BORDER_COLOR = "#333333";
const DEFAULT_BORDER_STYLE = "solid" as const;
const DEFAULT_BORDER_WIDTH = 1;
const REFERENCE_CONTAINER_WIDTH_PT = 957; // Default content width in px for px->% conversion
const PX_PERCENT_SCALE = 0.4; // Scale px->%

const styles = StyleSheet.create({
  // react-pdf supports display: table, even if TS complains in some setups
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: DEFAULT_BORDER_STYLE,
    borderColor: DEFAULT_BORDER_COLOR,
    borderWidth: DEFAULT_BORDER_WIDTH,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    color: "#000000"
  },
  row: {
    flexDirection: "row",
    width: "100%",
    color: "#000000"
  },
  cellContainer: {
    borderStyle: DEFAULT_BORDER_STYLE,
    borderColor: DEFAULT_BORDER_COLOR,
    borderWidth: DEFAULT_BORDER_WIDTH,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 6.5,
    paddingRight: 6.5,
    paddingTop: 0,
    paddingBottom: 0,
    flexShrink: 0,
    flexGrow: 0,
    color: "#000000"
  },
  cellText: {
    marginTop: 5,
    marginBottom: 5,
    fontSize: 10,
    color: "#000000"
  },
  cellTextHeader: {
    marginTop: 5,
    marginBottom: 5,
    fontSize: 12,
    fontWeight: 500,
    color: "#000000"
  }
});

function clampTo100(values: number[]): number[] {
  // Normalize to sum 100 preserving ratios with two-decimal precision
  const numCols = Math.max(1, values.length);
  const sum = values.reduce((a, b) => a + b, 0);
  if (sum === 0) {
    const equal = 100 / numCols;
    const baseHundredths = Math.floor(equal * 100);
    const remainderHundredths = 10000 - baseHundredths * numCols;
    const result: number[] = new Array(numCols).fill(baseHundredths);
    for (let i = 0; i < remainderHundredths; i++) result[i % numCols] += 1;
    return result.map((v) => v / 100);
  }
  const scaled = values.map((v) => (v / sum) * 100);
  const floorHundredths = scaled.map((v) => Math.floor(v * 100));
  const sumHundredths = floorHundredths.reduce((a, b) => a + b, 0);
  let remainderHundredths = 10000 - sumHundredths;
  // Distribute remainder as evenly as possible starting from index 0
  const resultHundredths = [...floorHundredths];
  if (remainderHundredths > 0) {
    const base = Math.floor(remainderHundredths / numCols);
    const extra = remainderHundredths % numCols;
    for (let i = 0; i < numCols; i++) {
      resultHundredths[i] += base + (i < extra ? 1 : 0);
    }
  }
  return resultHundredths.map((v) => v / 100);
}

function clampTo100WithLock(values: number[], lockMask: boolean[]): number[] {
  const numCols = Math.max(1, values.length);
  const sum = values.reduce((a, b) => a + b, 0);
  if (sum === 0) return clampTo100(values);
  const scaled = values.map((v) => (v / sum) * 100);
  const floorHundredths = scaled.map((v) => Math.floor(v * 100));
  let remainderHundredths = 10000 - floorHundredths.reduce((a, b) => a + b, 0);
  const resultHundredths = [...floorHundredths];

  const adjustableIndexes = lockMask.some((l) => !l)
    ? lockMask.map((l, i) => (!l ? i : -1)).filter((i) => i >= 0)
    : Array.from({ length: numCols }, (_, i) => i);

  if (remainderHundredths > 0) {
    const base = Math.floor(remainderHundredths / adjustableIndexes.length);
    const extra = remainderHundredths % adjustableIndexes.length;
    for (let k = 0; k < adjustableIndexes.length; k++) {
      const i = adjustableIndexes[k];
      resultHundredths[i] += base + (k < extra ? 1 : 0);
    }
  }
  return resultHundredths.map((v) => v / 100);
}

const WIDTH_DECL_RE = /(?:^|;)\s*width\s*:\s*([^;]+)/i;

function parseWidthFromStyleOrAttr($el: cheerio.Cheerio): {
  value: number;
  unit: "percent" | "px" | "none";
} {
  const styleAttr = (($el.attr("style") as string) || "").toString();
  const styleWidthMatch = styleAttr.match(WIDTH_DECL_RE);
  const styleWidth = styleWidthMatch ? styleWidthMatch[1].trim() : "";
  const widthAttr = (
    ($el.attr("width") as string) ||
    styleWidth ||
    ""
  ).toString();
  if (!widthAttr) return { value: 0, unit: "none" };
  if (widthAttr.endsWith("%")) {
    const v = parseFloat(widthAttr.replace("%", ""));
    return Number.isFinite(v)
      ? { value: v, unit: "percent" }
      : { value: 0, unit: "none" };
  }
  const px = parseFloat(widthAttr.replace("px", ""));
  if (Number.isFinite(px)) return { value: px, unit: "px" };
  return { value: 0, unit: "none" };
}

function parseCellTextStyles(styleAttr: string): {
  fontSizePx?: number;
  italic?: boolean;
  textAlign?: "left" | "center" | "right";
  backgroundColor?: string;
  textColor?: string;
} {
  const out: {
    fontSizePx?: number;
    italic?: boolean;
    textAlign?: "left" | "center" | "right";
    backgroundColor?: string;
    textColor?: string;
  } = {};
  const style = styleAttr || "";
  const fontSizeMatch = style.match(/font-size\s*:\s*([^;]+)/i);
  if (fontSizeMatch) {
    const raw = fontSizeMatch[1].trim();
    const px = parseFloat(raw.replace("px", "").replace("pt", ""));
    if (Number.isFinite(px)) out.fontSizePx = px;
  }
  const fontStyleMatch = style.match(/font-style\s*:\s*([^;]+)/i);
  if (fontStyleMatch) {
    const v = fontStyleMatch[1].trim().toLowerCase();
    if (v.includes("italic")) out.italic = true;
  }
  const alignMatch = style.match(/text-align\s*:\s*([^;]+)/i);
  if (alignMatch) {
    const v = alignMatch[1].trim().toLowerCase();
    if (v === "left" || v === "center" || v === "right") out.textAlign = v;
  }
  const bgMatch = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
  if (bgMatch) {
    out.backgroundColor = bgMatch[1].trim();
  }
  const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  if (colorMatch) {
    out.textColor = colorMatch[1].trim();
  }
  return out;
}

function parseRowStyles(styleAttr: string): {
  backgroundColor?: string;
  textColor?: string;
} {
  const style = styleAttr || "";
  const out: { backgroundColor?: string; textColor?: string } = {};
  const bgMatch = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
  if (bgMatch) out.backgroundColor = bgMatch[1].trim();
  const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  if (colorMatch) out.textColor = colorMatch[1].trim();
  return out;
}

function deriveColumnPercentsFromColgroup(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  maxCols: number
): number[] | null {
  const $colgroup = $table.find("colgroup").first();
  if ($colgroup.length === 0) return null;

  const colWidths: number[] = [];
  $colgroup.find("col").each((_: number, col: cheerio.Element) => {
    const spec = parseWidthFromStyleOrAttr($(col));
    if (spec.unit === "percent" && spec.value > 0) {
      colWidths.push(spec.value);
    } else {
      colWidths.push(0); // Will be filled later
    }
  });

  if (colWidths.length !== maxCols) return null;

  // Fill any zero-width columns with equal distribution of remaining space
  const totalSpecified = colWidths.reduce((a, b) => a + b, 0);
  const unspecifiedCount = colWidths.filter((w) => w === 0).length;

  if (totalSpecified < 100 && unspecifiedCount > 0) {
    const remainingPercent = 100 - totalSpecified;
    const equalShare = remainingPercent / unspecifiedCount;
    for (let i = 0; i < colWidths.length; i++) {
      if (colWidths[i] === 0) {
        colWidths[i] = equalShare;
      }
    }
  }

  return clampTo100(colWidths);
}

function deriveColumnPercentsFromTd(
  $: cheerio.Root,
  $table: cheerio.Cheerio,
  maxCols: number,
  referenceWidthPx: number
): number[] | null {
  // Find the first row that provides any width info
  let selectedRow: cheerio.Element | null = null;
  $table.find("tr").each((_: number, tr: cheerio.Element) => {
    if (selectedRow) return;
    const hasWidth = $(tr)
      .children("th,td")
      .toArray()
      .some((cell) => {
        const spec = parseWidthFromStyleOrAttr($(cell as any));
        return spec.unit !== "none" && spec.value > 0;
      });
    if (hasWidth) selectedRow = tr;
  });

  if (!selectedRow) return null;

  type CellSpec = {
    colspan: number;
    unit: "percent" | "px" | "none";
    value: number;
  };
  const cellSpecs: CellSpec[] = [];
  $(selectedRow)
    .children("th,td")
    .each((__: number, cell: cheerio.Element) => {
      const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
      const spec = parseWidthFromStyleOrAttr($(cell));
      cellSpecs.push({ colspan, unit: spec.unit, value: spec.value });
    });

  // Use the provided reference width directly to ensure identical px-based columns across tables
  const effectiveRef = referenceWidthPx;

  // Convert cell specs to percent values (pre-distribution), separating px and percent
  type PercentSpec = { colspan: number; percent: number; isPx: boolean };
  const percentSpecs: PercentSpec[] = cellSpecs.map((s) => {
    if (s.unit === "percent") {
      return { colspan: s.colspan, percent: Math.max(0, s.value), isPx: false };
    }
    if (s.unit === "px") {
      const pct =
        Math.max(0, (s.value / effectiveRef) * 100) * PX_PERCENT_SCALE;
      return { colspan: s.colspan, percent: pct, isPx: true };
    }
    return { colspan: s.colspan, percent: 0, isPx: false };
  });

  // If both px and percent are present and the sum exceeds 100,
  // scale down percent-based totals so px-based totals remain intact
  const pxTotal = percentSpecs
    .filter((p) => p.isPx)
    .reduce((a, b) => a + b.percent, 0);
  const pctTotal = percentSpecs
    .filter((p) => !p.isPx)
    .reduce((a, b) => a + b.percent, 0);
  if (pxTotal > 0 && pctTotal > 0 && pxTotal + pctTotal > 100) {
    const allowedForPct = Math.max(0, 100 - pxTotal);
    const scale = pctTotal > 0 ? allowedForPct / pctTotal : 1;
    for (const p of percentSpecs) {
      if (!p.isPx) p.percent = p.percent * scale;
    }
  }

  const colValues = new Array(Math.max(1, maxCols)).fill(0);
  const specifiedMask = new Array(Math.max(1, maxCols)).fill(false);
  const pxSpecifiedMask = new Array(Math.max(1, maxCols)).fill(false);

  let colCursor = 0;
  for (const spec of percentSpecs) {
    const { colspan, percent, isPx } = spec;
    const perCol = percent / Math.max(1, colspan);
    for (let i = 0; i < colspan; i++) {
      const idx = colCursor + i;
      if (idx < colValues.length) {
        colValues[idx] += perCol;
        if (percent > 0) specifiedMask[idx] = true;
        if (isPx && percent > 0) pxSpecifiedMask[idx] = true;
      }
    }
    colCursor += Math.max(1, colspan);
  }

  const totalSpecified = colValues.reduce((a, b) => a + b, 0);
  const unspecifiedIndexes: number[] = [];
  for (let i = 0; i < colValues.length; i++) {
    if (!specifiedMask[i]) unspecifiedIndexes.push(i);
  }

  if (totalSpecified <= 0) return null;

  if (totalSpecified < 100 && unspecifiedIndexes.length > 0) {
    const remainder = 100 - totalSpecified;
    const add = remainder / unspecifiedIndexes.length;
    for (const idx of unspecifiedIndexes) colValues[idx] = add;
  }

  // Preserve px-based columns during rounding so identical px widths look identical across tables
  return clampTo100WithLock(colValues, pxSpecifiedMask);
}

function detectMaxColumns($: cheerio.Root, $table: cheerio.Cheerio): number {
  let maxCols = 0;
  $table.find("tr").each((_: number, tr: cheerio.Element) => {
    let cols = 0;
    $(tr)
      .children("th,td")
      .each((__: number, cell: cheerio.Element) => {
        const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
        cols += Math.max(1, colspan);
      });
    if (cols > maxCols) maxCols = cols;
  });

  // Also check if any row has cells that explicitly define a higher column count
  // by looking at the first row (header row) which should define the base structure
  const firstRow = $table.find("tr").first();
  if (firstRow.length > 0) {
    const headerCells = firstRow.children("th,td").length;
    if (headerCells > maxCols) maxCols = headerCells;
  }

  return maxCols || 1;
}

function equalSplit(count: number): number[] {
  return clampTo100(new Array(Math.max(1, count)).fill(1));
}

export function parseHtmlTableToDescription(
  html: string,
  options?: { containerWidthPx?: number }
): PdfTableDescription | null {
  const loadFn = cheerio.load as any;
  const $ = loadFn(html);
  const $table = $("table").first();
  if ($table.length === 0) return null;

  // Establish column widths
  const maxCols = detectMaxColumns($, $table);
  const referenceWidthPx =
    options?.containerWidthPx ?? REFERENCE_CONTAINER_WIDTH_PT;

  // Try colgroup first, then fall back to TD-based detection
  let colPercents = deriveColumnPercentsFromColgroup($, $table, maxCols);
  if (!colPercents) {
    colPercents = deriveColumnPercentsFromTd(
      $,
      $table,
      maxCols,
      referenceWidthPx
    );
  }
  if (!colPercents) {
    colPercents = equalSplit(maxCols);
  }

  const rows: PdfTableRow[] = [];
  $table.find("tr").each((_: number, tr: cheerio.Element) => {
    const rowCells: PdfTableCell[] = [];
    const rowStyleInherit = parseRowStyles(
      ($(tr).attr("style") as string) || ""
    );
    $(tr)
      .children("th,td")
      .each((__: number, cell: cheerio.Element) => {
        const tag = ($(cell).get(0)?.tagName || "").toLowerCase();
        const isHeader = tag === "th";
        const colspan = parseInt($(cell).attr("colspan") || "1", 10) || 1;
        // Extract content
        const innerHtml = $(cell).html() || "";
        let content = $(cell).text();
        content = content.replace(/\u00A0/g, " ").trim();
        // Parse per-cell text styles
        const styles = parseCellTextStyles(
          ($(cell).attr("style") as string) || ""
        );

        rowCells.push({
          content,
          rawHtml: innerHtml,
          colspan: Math.max(1, colspan),
          isHeader,
          fontSizePx: styles.fontSizePx,
          italic: styles.italic,
          textAlign: styles.textAlign,
          backgroundColor:
            styles.backgroundColor || rowStyleInherit.backgroundColor,
          textColor: styles.textColor || rowStyleInherit.textColor || "#000000"
        });
      });

    rows.push({ cells: rowCells });
  });

  return {
    columns: colPercents,
    rows,
    borderColor: DEFAULT_BORDER_COLOR,
    borderStyle: DEFAULT_BORDER_STYLE,
    borderWidth: DEFAULT_BORDER_WIDTH
  };
}

function computeCellWidthPercent(
  columns: number[],
  startColIndex: number,
  colspan: number
): number {
  const totalCols = columns.length;
  const span = Math.max(1, colspan);

  // Ensure we don't start beyond the table
  if (startColIndex >= totalCols) return 0;

  // Calculate the actual end index
  const end = Math.min(totalCols, startColIndex + span);
  let sum = 0;
  for (let i = startColIndex; i < end; i++) {
    sum += columns[i] || 0;
  }
  return sum;
}

function allocateWidthForCell(
  columns: number[],
  colCursor: number,
  span: number,
  rowAccumulated: number
): { width: number; nextCursor: number } {
  const totalCols = columns.length;
  const remainingCols = Math.max(0, totalCols - colCursor);
  const requested = Math.max(1, span);

  // If we're at or beyond the total columns, don't allocate any width
  if (colCursor >= totalCols) {
    return { width: 0, nextCursor: colCursor };
  }

  // Calculate actual span considering remaining columns
  const actualSpan = Math.min(requested, remainingCols);
  const width = computeCellWidthPercent(columns, colCursor, actualSpan);
  return { width, nextCursor: colCursor + actualSpan };
}

export default function PdfDocumentTable({
  description
}: {
  description: PdfTableDescription;
}) {
  const borderColor = description.borderColor || DEFAULT_BORDER_COLOR;
  const borderStyle = description.borderStyle || DEFAULT_BORDER_STYLE;
  const borderWidth = description.borderWidth ?? DEFAULT_BORDER_WIDTH;
  return (
    <View
      style={{
        ...styles.table,
        borderColor,
        borderStyle,
        borderWidth
      }}
    >
      {description.rows.map((row, rowIndex) => {
        return (
          <View key={rowIndex} style={styles.row}>
            {(() => {
              let colCursor = 0;
              let rowAccumulated = 0;
              return row.cells.map((cell, cellIndex) => {
                const { width, nextCursor } = allocateWidthForCell(
                  description.columns,
                  colCursor,
                  cell.colspan,
                  rowAccumulated
                );
                rowAccumulated += width;
                colCursor = nextCursor;
                return (
                  <View
                    key={cellIndex}
                    style={{
                      ...styles.cellContainer,
                      borderColor,
                      borderStyle,
                      borderWidth,
                      width: `${width}%`,
                      minWidth: `${width}%`,
                      maxWidth: `${width}%`,
                      backgroundColor: cell.backgroundColor
                    }}
                  >
                    {cell.rawHtml && /<[^>]+>/.test(cell.rawHtml) ? (
                      <Html>
                        {`<html><body><style>${DEFAULT_PDF_CSS_STYLE}</style><div style="${[
                          cell.textAlign ? `text-align: ${cell.textAlign}` : "",
                          cell.italic ? "font-style: italic" : "",
                          cell.fontSizePx
                            ? `font-size: ${cell.fontSizePx}px`
                            : "",
                          `color: ${cell.textColor ?? "#000000"}`,
                          cell.backgroundColor
                            ? `background-color: ${cell.backgroundColor}`
                            : ""
                        ]
                          .filter(Boolean)
                          .join(
                            ";"
                          )}"><span style="color: ${cell.textColor ?? "#000000"}">${cell.rawHtml}</span></div></body></html>`}
                      </Html>
                    ) : (
                      <Text
                        style={{
                          ...(cell.isHeader
                            ? (styles.cellTextHeader as any)
                            : (styles.cellText as any)),
                          textAlign: cell.textAlign,
                          fontStyle: cell.italic ? "italic" : undefined,
                          fontSize: cell.fontSizePx ?? undefined,
                          color: cell.textColor ?? "#000000"
                        }}
                      >
                        {cell.content || (cell.rawHtml ?? "")}
                      </Text>
                    )}
                  </View>
                );
              });
            })()}
          </View>
        );
      })}
    </View>
  );
}

export function buildTableDescriptionFromHtml(
  html: string,
  options?: { containerWidthPx?: number }
): PdfTableDescription | null {
  return parseHtmlTableToDescription(html, options);
}
